import { forwardRef } from 'react'
import { evaluateSection, summarizeRules, holidayRegime } from '../../../shared/freeNow.js'
import { CATEGORY_COLOR, paymentUrlFor } from '../../../shared/model.js'
import { SOON_MIN } from '../lib/mapStyle.js'
import { humanizeMinutes, transitionLabel } from '../lib/i18n.js'
import { IconCard, IconClock, IconClose, IconNav } from './Icons.jsx'
import StayChips from './StayChips.jsx'
import StaySlider from './StaySlider.jsx'

/** Everything the card shows about one section, computed once. */
export function sectionModel({ section, effectiveDate, lang, t, estimate, durationMin, center }) {
  const status = evaluateSection(section, effectiveDate, lang)
  const holiday = holidayRegime(section, lang)
  const dur = durationMin ? humanizeMinutes(durationMin, t) : null

  // Some sections have different day/evening tariffs — show the honest range.
  const rulePrices = [...new Set((section.rules || []).map((r) => r.price).filter((p) => p != null))]
  const priceLine =
    status.activeRule?.price != null
      ? t('pricePerHour', status.activeRule.price)
      : rulePrices.length > 1
        ? t('pricePerHourRange', Math.min(...rulePrices), Math.max(...rulePrices))
        : (section.price ?? rulePrices[0]) != null
          ? t('pricePerHour', section.price ?? rulePrices[0])
          : null
  const maxHours = status.activeRule?.maxHours ?? section.maxHours ?? section.rules?.[0]?.maxHours ?? null

  // The headline: the price of YOUR stay when you've picked one, else the status.
  let tone, main, sub
  if (estimate && durationMin) {
    if (!estimate.allowed) {
      tone = 'blocked'
      main = t('blockedTitle')
      sub = t('blockedSub', estimate.maxHours)
    } else if (estimate.fullyFree) {
      tone = 'free'
      main = t('heatFree')
      sub = t('freeWholeStay', dur)
    } else {
      tone = 'paid'
      main = t('price', estimate.cost)
      sub = t('forStay', dur)
    }
  } else if (status.free) {
    tone = 'free'
    main = t('cardFree')
    sub = status.neverCharged ? t('neverCharged') : null
  } else if (status.changesInMin != null && status.changesInMin <= SOON_MIN) {
    tone = 'soon'
    main = t('cardSoon')
    sub = priceLine
  } else {
    tone = 'paid'
    main = t('cardPaid')
    sub = priceLine
  }

  const breakLine =
    estimate && durationMin && estimate.allowed && !estimate.fullyFree && estimate.freeMinutes > 0
      ? t('costBreak', humanizeMinutes(estimate.paidMinutes, t), humanizeMinutes(estimate.freeMinutes, t))
      : null

  let changeLine
  if (status.neverCharged) changeLine = t('neverCharged')
  else if (status.changesInMin == null) changeLine = t('alwaysToday')
  else {
    const when = transitionLabel(status.changesAt)
    const left = humanizeMinutes(status.changesInMin, t)
    changeLine = status.free
      ? `${t('freeUntil', when)} · ${t('freeFor', left)}`
      : `${t('paidUntil', when)} · ${t('paidFor', left)}`
  }

  const navigateUrl = center
    ? `https://www.google.com/maps/dir/?api=1&destination=${center.lat.toFixed(6)},${center.lng.toFixed(6)}&travelmode=driving`
    : null
  // The bundled snapshot still carries the retired ke-utc.appspot.com links (all
  // 404 now): derive the official pay URL unless a stored one is already live.
  const payUrl =
    section.paymentUrl && !section.paymentUrl.includes('ke-utc.appspot.com')
      ? section.paymentUrl
      : paymentUrlFor(section.id)

  const capNote =
    !holiday.free &&
    (section.category === 'mixed' || section.category === 'visitor') &&
    new Set((section.rules || []).flatMap((r) => r.days)).size < 7

  const isPrice = !!(estimate && durationMin && estimate.allowed && !estimate.fullyFree)

  return { status, holiday, priceLine, maxHours, tone, main, sub, isPrice, breakLine, changeLine, navigateUrl, payUrl, capNote }
}

/** Card header: title, place, and the headline price/status (visible at peek). */
export const SectionHeader = forwardRef(function SectionHeader({ t, section, model, onClose }, titleRef) {
  return (
    <div className="card-head">
      <div className="card-head__top">
        <div className="card-head__titles">
          <h2 ref={titleRef} tabIndex={-1} className="card-head__title">
            {t('section')} {section.id}
          </h2>
          <p className="card-head__sub">
            <span className="dot" style={{ background: CATEGORY_COLOR[section.category] }} aria-hidden="true" />
            {[section.district, t(`${section.category}Short`)].filter(Boolean).join(' · ')}
          </p>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('close')}>
          <IconClose />
        </button>
      </div>
      <p className={`hero hero--${model.tone} ${model.isPrice ? 'hero--price' : ''}`}>
        <span className="hero__main">{model.main}</span>
        {model.sub && <span className="hero__sub">{model.sub}</span>}
      </p>
    </div>
  )
})

/** Card body: what changes when, re-price for another stay, go/pay, the rules. */
export function SectionBody({ t, lang, section, model, durationText, setDurationText, durationMin, effectiveDate }) {
  const m = model
  return (
    <div className="card-body">
      {m.breakLine && <p className="card-body__break">{m.breakLine}</p>}
      <p className="card-row">
        <IconClock className="card-row__icon" />
        <span>{m.changeLine}</span>
      </p>

      <section className="section">
        <h3 className="section__title">{t('stayLabel')}</h3>
        <StayChips t={t} lang={lang} durationText={durationText} setDurationText={setDurationText} />
        <StaySlider t={t} durationMin={durationMin} startDate={effectiveDate} onChange={setDurationText} />
      </section>

      <div className="actions">
        {m.navigateUrl && (
          <a className="btn btn--primary" href={m.navigateUrl} target="_blank" rel="noopener noreferrer">
            <IconNav />
            {t('navigate')}
          </a>
        )}
        <a className="btn btn--secondary" href={m.payUrl} target="_blank" rel="noopener noreferrer">
          <IconCard />
          {t('pay')}
        </a>
      </div>

      {m.status.holiday && <p className="note note--warn">{t('holidayToday', m.status.holidayName)}</p>}

      <section className="section">
        <h3 className="section__title">{t('rulesAndPrices')}</h3>
        <dl className="facts">
          <div className="facts__row">
            <dt>{t('weekdaysLabel')}</dt>
            <dd>{summarizeRules(section, lang)}</dd>
          </div>
          <div className="facts__row">
            <dt>{t('holidaysLabel')}</dt>
            <dd className={m.holiday.free ? 'is-free' : undefined}>
              {m.holiday.text}
              {m.capNote && <span className="facts__note">{t('holidayCapNote')}</span>}
            </dd>
          </div>
          {m.priceLine && (
            <div className="facts__row">
              <dt>{t('priceLabel')}</dt>
              <dd>{m.priceLine}</dd>
            </div>
          )}
          {m.maxHours != null && (
            <div className="facts__row">
              <dt>{t('limitLabel')}</dt>
              <dd>{t('maxStay', m.maxHours)}</dd>
            </div>
          )}
          {section.places != null && (
            <div className="facts__row">
              <dt>{t('placesLabel')}</dt>
              <dd>{section.places}</dd>
            </div>
          )}
        </dl>
      </section>

      <p className="disclaimer">{t('disclaimer')}</p>
    </div>
  )
}
